import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, NavigationRightIcon, Text, useResponsive } from '@common-fe/common-fe';
import _ from 'lodash';

import { REGEXPS } from '@/common';
import { CURRENCY_SIGN } from '@/common/constants';
import { useMyEnrollments } from '@/modules/transaction';

import { InvestmentCardBlock } from '../../InvestmentCardBlock';
import SelfDirectedWrapper from '../SelfDirectedWrapper';

import { useAllocationInvestmentAmount, useSelfDirectedAllocationValidation } from './hooks';
import SelfDirectedAllocationFunds from './SelfDirectedAllocationFunds';
import SelfDirectedInvestAmount from './SelfDirectedInvestAmount';

interface Props {
  accountId: string;
  investorId?: string;
  isSwitchMode?: boolean;
  isEmptyPortfolioMode?: boolean;
}

const SelfDirectedAllocation: React.FC<Props> = ({
  isSwitchMode,
  isEmptyPortfolioMode,
  accountId,
  investorId,
}) => {
  const { isMobile } = useResponsive();
  const { value, setValue } = useAllocationInvestmentAmount();

  const [isSubmitted, setSubmitted] = useState(false);
  const valueToNumber = useMemo(() => {
    const clearedValue = value.replace(REGEXPS.ALL_COMMAS_REGEXP, '').replace(CURRENCY_SIGN, '');
    return _.toNumber(clearedValue);
  }, [value]);
  const { maxAmount, errorText } = useSelfDirectedAllocationValidation({
    accountId,
    investorId,
    value: valueToNumber,
  });
  const { currentMyAccount } = useMyEnrollments(accountId);
  const { t } = useTranslation();
  return (
    <SelfDirectedWrapper
      isSwitchMode={isSwitchMode}
      isEmptyPortfolioMode={isEmptyPortfolioMode}
      currentStep={isEmptyPortfolioMode ? 3 : 4}
    >
      <Box direction="column">
        <Text data-testid="SelfDirectedAllocation_title" size="3xl" weight={700}>
          {t('Set Fund Allocation')}
        </Text>
        <Text size="xxlarge" margin={{ top: 'l' }} weight={700}>
          {t('Funding Amount')}
        </Text>
      </Box>
      <Box direction="row" margin={{ top: 'l', bottom: 'l' }} background="module" round="moduleRound" pad="l">
        <InvestmentCardBlock
          {...(isMobile
            ? {
                width: {
                  min: 'auto',
                },
              }
            : {})}
          enrollment={currentMyAccount}
        />

        <Box pad={{ horizontal: 's' }} align="center" justify="center">
          <NavigationRightIcon />
        </Box>
        <Box justify="center">
          <SelfDirectedInvestAmount
            value={value}
            errorText={errorText}
            maxValue={maxAmount}
            isSubmitted={isSubmitted}
            onSetValue={setValue}
          />
        </Box>
      </Box>
      <SelfDirectedAllocationFunds
        isSwitchMode={isSwitchMode}
        isEmptyPortfolioMode={isEmptyPortfolioMode}
        isErrors={!!errorText}
        investorId={investorId}
        accountId={accountId}
        investAmount={valueToNumber}
        isSubmitted={isSubmitted}
        onSetSubmitted={setSubmitted}
      />
    </SelfDirectedWrapper>
  );
};

export default SelfDirectedAllocation;
